import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft,
  Send,
  Paperclip,
  Clock,
  CheckCircle,
  AlertCircle,
  User,
  Headphones,
  Image,
  X,
} from "lucide-react";
import Header from "@/components/Header";
import BottomBar from "@/components/BottomBar";

// Sample ticket data with conversation
const ticketData = {
  id: "TKT-2024-001",
  subject: "Card activation issue",
  category: "Card Issues",
  status: "open",
  priority: "high",
  createdAt: "2024-03-15 14:30",
  lastUpdate: "2 hours ago",
  conversation: [
    {
      id: 1,
      type: "user",
      message: "I'm trying to activate my new card but the activation page keeps showing an error. I've entered all the correct information including the card number, expiry date, and CVV.",
      timestamp: "2024-03-15 14:30",
      attachments: [
        { id: 1, name: "error-screenshot.png", type: "image/png" }
      ]
    },
    {
      id: 2,
      type: "support",
      name: "Support Agent",
      message: "Thank you for contacting us. I can see you're having trouble activating your card. Let me look into this for you. Can you please confirm the last 4 digits of your card number?",
      timestamp: "2024-03-15 14:45",
    },
    {
      id: 3,
      type: "user",
      message: "The last 4 digits are 1572. I've tried multiple times but keep getting the same error.",
      timestamp: "2024-03-15 14:50",
    },
    {
      id: 4,
      type: "support",
      name: "Support Agent",
      message: "I've checked your account and found the issue. Your card activation was blocked due to a security check. I've now cleared this and you should be able to activate your card. Please try again and let me know if you still face any issues.",
      timestamp: "2024-03-15 15:30",
    }
  ]
};

export default function SupportTicketDetail() {
  const navigate = useNavigate();
  const { ticketId } = useParams();
  const [replyMessage, setReplyMessage] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSendReply = () => {
    if (!replyMessage.trim() && attachments.length === 0) return;
    
    setIsSubmitting(true);
    
    // Simulate sending reply
    setTimeout(() => {
      setReplyMessage("");
      setAttachments([]);
      setIsSubmitting(false);
      // In real app, would add the new message to conversation
    }, 1000);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files).filter(file => 
      file.type.startsWith('image/')
    );
    
    if (files.length > 0) {
      const newAttachments = files.map(file => ({
        id: Math.random().toString(36).substr(2, 9),
        name: file.name,
        type: file.type,
        file: file
      }));
      setAttachments(prev => [...prev, ...newAttachments]);
    }
  };

  const removeAttachment = (attachmentId) => {
    setAttachments(prev => prev.filter(a => a.id !== attachmentId));
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "open":
        return <Clock className="w-4 h-4" />;
      case "in_progress":
        return <AlertCircle className="w-4 h-4" />;
      case "resolved":
        return <CheckCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "open":
        return "text-blue-600 dark:text-blue-400";
      case "in_progress":
        return "text-yellow-600 dark:text-yellow-400";
      case "resolved":
        return "text-green-600 dark:text-green-400";
      default:
        return "text-muted-foreground";
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "low":
        return "text-blue-600 dark:text-blue-400";
      case "medium":
        return "text-yellow-600 dark:text-yellow-400";
      case "high":
        return "text-orange-600 dark:text-orange-400";
      case "urgent":
        return "text-red-600 dark:text-red-400";
      default:
        return "text-muted-foreground";
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header showBack={true} onBackClick={() => navigate("/support-ticket")} />

      <div className="max-w-md mx-auto px-3 pb-20">
        {/* Ticket Header */}
        <Card className="mt-4 bg-card/80">
          <CardContent className="p-4">
            <div className="space-y-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-xs font-medium text-muted-foreground mb-1">
                    {ticketData.id}
                  </p>
                  <h2 className="text-lg font-semibold">{ticketData.subject}</h2>
                </div>
                <span className={`text-xs font-semibold ${getPriorityColor(ticketData.priority)}`}>
                  {ticketData.priority.toUpperCase()}
                </span>
              </div>
              
              <div className="flex items-center justify-between pt-2 border-t border-border/20">
                <div className={`flex items-center gap-1 ${getStatusColor(ticketData.status)}`}>
                  {getStatusIcon(ticketData.status)}
                  <span className="text-xs font-medium">
                    {ticketData.status.replace("_", " ").toUpperCase()}
                  </span>
                </div>
                <span className="text-xs text-muted-foreground">
                  Created: {ticketData.createdAt}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Conversation */}
        <div className="mt-4 space-y-3">
          {ticketData.conversation.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.type === "user" ? "justify-end" : "justify-start"}`}
            >
              <div className={`max-w-[85%] ${message.type === "user" ? "order-2" : ""}`}>
                <div className="flex items-center gap-2 mb-1">
                  {message.type === "support" && (
                    <div className="p-1.5 bg-primary/10 rounded-full">
                      <Headphones className="w-3 h-3 text-primary" />
                    </div>
                  )}
                  <span className="text-xs font-medium text-muted-foreground">
                    {message.type === "user" ? "You" : message.name}
                  </span>
                  {message.type === "user" && (
                    <div className="p-1.5 bg-muted rounded-full">
                      <User className="w-3 h-3 text-muted-foreground" />
                    </div>
                  )}
                </div>
                
                <Card className={`${
                  message.type === "user" 
                    ? "bg-primary text-primary-foreground" 
                    : "bg-card/80"
                }`}>
                  <CardContent className="p-3">
                    <p className="text-sm">{message.message}</p>
                    
                    {message.attachments && message.attachments.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {message.attachments.map((attachment) => (
                          <div
                            key={attachment.id}
                            className={`flex items-center gap-2 p-2 rounded ${
                              message.type === "user"
                                ? "bg-primary-foreground/10"
                                : "bg-muted"
                            }`}
                          >
                            <Image className="w-4 h-4" />
                            <span className="text-xs">{attachment.name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    <p className={`text-xs mt-2 ${
                      message.type === "user"
                        ? "text-primary-foreground/70"
                        : "text-muted-foreground"
                    }`}>
                      {message.timestamp}
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          ))}
        </div>

        {/* Reply Section - Only show if ticket is not resolved */}
        {ticketData.status !== "resolved" && (
          <Card className="mt-6 bg-card/80 sticky bottom-20">
            <CardContent className="p-4 space-y-3">
              <Textarea
                placeholder="Type your reply..."
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                className="min-h-[80px] resize-none"
              />
              
              {/* Attachments */}
              {attachments.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {attachments.map((attachment) => (
                    <div
                      key={attachment.id}
                      className="flex items-center gap-1 px-2 py-1 bg-muted rounded-lg"
                    >
                      <Image className="w-3 h-3" />
                      <span className="text-xs truncate max-w-[100px]">
                        {attachment.name}
                      </span>
                      <button
                        onClick={() => removeAttachment(attachment.id)}
                        className="ml-1 hover:text-destructive"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="flex gap-2">
                <input
                  type="file"
                  id="file-upload"
                  multiple
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => document.getElementById("file-upload").click()}
                >
                  <Paperclip className="w-4 h-4" />
                </Button>
                
                <Button
                  onClick={handleSendReply}
                  disabled={(!replyMessage.trim() && attachments.length === 0) || isSubmitting}
                  className="flex-1"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {isSubmitting ? "Sending..." : "Send Reply"}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Resolved Notice */}
        {ticketData.status === "resolved" && (
          <Card className="mt-6 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                <div>
                  <p className="text-sm font-semibold text-green-800 dark:text-green-200">
                    This ticket has been resolved
                  </p>
                  <p className="text-xs text-green-700 dark:text-green-300 mt-1">
                    If you need further assistance, please create a new ticket.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <BottomBar />
    </div>
  );
}